import React, { useEffect, useState } from 'react';
import { applyTheme, getThemePref, persistTheme, watchSystemTheme, type ResolvedTheme, type ThemePref } from '../theme';

export interface ThemeChoiceLabels {
  label?: string;
  light?: string;
  dark?: string;
  system?: string;
}

/**
 * Theme picker as an inline segmented control, for use *inside* another menu.
 *
 * <p>
 * The dropdown version ({@link ThemeMenu}) cannot live in the user menu — a dropdown inside a
 * dropdown is a trap on touch, where the outer one usually closes on the first tap. This shows all
 * three options at once instead, which also removes a tap: the choice is one press rather than
 * open-then-pick.
 * </p>
 *
 * <p>
 * Still shows what the screen is currently doing when `system` is selected, because that is the
 * question a user actually has when the app is dark and they did not choose dark.
 * </p>
 */
export const ThemeChoice: React.FC<{ labels?: ThemeChoiceLabels }> = ({ labels }) => {
  const [pref, setPref] = useState<ThemePref>(() => getThemePref());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => applyTheme(getThemePref()));

  useEffect(() => watchSystemTheme(setResolved), []);

  const choose = (next: ThemePref) => {
    setPref(next);
    setResolved(persistTheme(next));
  };

  const options: { value: ThemePref; label: string }[] = [
    { value: 'light', label: labels?.light ?? 'Light' },
    { value: 'dark', label: labels?.dark ?? 'Dark' },
    { value: 'system', label: labels?.system ?? 'Auto' },
  ];

  return (
    <div className="px-3 py-2">
      <p className="mb-1.5 text-xs font-medium text-subtle-ink">{labels?.label ?? 'Theme'}</p>
      <div role="group" aria-label={labels?.label ?? 'Theme'} className="flex rounded-lg border border-line p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={o.value === pref}
            onClick={() => choose(o.value)}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition ${
              o.value === pref ? 'bg-brand-600 text-white' : 'text-muted-strong hover:bg-subtle'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {pref === 'system' && (
        <p className="mt-1 text-[11px] text-subtle-ink">
          {resolved === 'dark' ? (labels?.dark ?? 'Dark') : (labels?.light ?? 'Light')}
        </p>
      )}
    </div>
  );
};
